import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CompanyMembership } from '@/types/user.types';

interface CompanyStore {
  currentCompanyId: string | null;
  memberships: CompanyMembership[];
  isLoading: boolean;
  isSwitching: boolean;
  setCurrentCompany: (companyId: string) => void;
  setMemberships: (memberships: CompanyMembership[]) => void;
  clearCompany: () => void;
  refreshMemberships: () => Promise<void>;
  setSwitching: (isSwitching: boolean) => void;
}

export const useCompanyStore = create<CompanyStore>()(
  persist(
    (set, get) => ({
      currentCompanyId: null,
      memberships: [],
      isLoading: false,
      isSwitching: false,
      setSwitching: (isSwitching: boolean) => {
        set({ isSwitching });
      },
      setCurrentCompany: (companyId: string) => {
        set({ currentCompanyId: companyId });
      },
      setMemberships: (memberships: CompanyMembership[]) => {
        set({ memberships });
        if (!get().currentCompanyId && memberships.length > 0) {
          set({ currentCompanyId: memberships[0].companyId });
        }
      },
      clearCompany: () => {
        set({ currentCompanyId: null, memberships: [] });
      },
      refreshMemberships: async () => {
        set({ isLoading: true });
        try {
          const { companyMembershipService } = await import('@/services/company-membership/company-membership.service');
          const memberships = await companyMembershipService.getUserMemberships();
          set({ memberships, isLoading: false });
          const currentId = get().currentCompanyId;
          
          // Use current company if still valid, otherwise use first membership
          if (currentId && memberships.find((m) => m.companyId === currentId)) {
            // Keep current company
          } else if (memberships.length > 0) {
            set({ currentCompanyId: memberships[0].companyId });
          } else {
            set({ currentCompanyId: null });
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },
    }),
    {
      name: 'company-store',
      partialize: (state) => ({
        currentCompanyId: state.currentCompanyId,
        memberships: state.memberships,
      }),
    }
  )
);

